import { memo, useState, type ComponentType, type MouseEvent } from "react";

import { Edit, Tag as TagIcon, Trash2 } from "lucide-react";

import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import PostLikes from "./PostLikes";
import type { PostMetrics } from "@/hooks/usePostMetrics";

export interface BlogPostListItemPost {
  id: string;
  title: string;
  preview: string;
  content: string;
  image_url?: string | null;
  facebook_url?: string;
  twitter_url?: string;
  instagram_url?: string;
  linkedin_url?: string;
  youtube_url?: string;
  tiktok_url?: string;
  threads_url?: string;
  google_business_url?: string;
  pinterest_url?: string;
  snapchat_url?: string;
  bluesky_url?: string;
  reddit_url?: string;
  substack_url?: string;
  discord_url?: string;
  created_at: string;
  published_at?: string;
  published: boolean;
  user_id: string;
  post_tags?: {
    tag_id: string;
    tags: {
      id: string;
      name: string;
      slug: string;
    };
  }[];
}

interface BlogPostListItemProps {
  post: BlogPostListItemPost;
  index: number;
  isAdmin: boolean;
  onPostClick: (post: BlogPostListItemPost) => void;
  onTogglePublish: (post: BlogPostListItemPost, event: MouseEvent) => Promise<void> | void;
  onEditPost: (post: BlogPostListItemPost) => void;
  onDeletePost: (post: BlogPostListItemPost, event: MouseEvent) => Promise<void> | void;
  onTagClick: (slug: string) => void;
  getMetrics: (postId: string) => PostMetrics | undefined;
  getSocialIcon: (platform: string) => string | ComponentType<{ className?: string }> | null;
  formatDate: (dateString: string) => string;
}

const BlogPostListItem = ({
  post,
  index,
  isAdmin,
  onPostClick,
  onTogglePublish,
  onEditPost,
  onDeletePost,
  onTagClick,
  getMetrics,
  getSocialIcon,
  formatDate,
}: BlogPostListItemProps) => {
  const [hasNewActivity, setHasNewActivity] = useState(false);

  return (
    <div
      className={`border rounded-lg p-4 cursor-pointer hover:bg-muted/50 transition-colors overflow-hidden relative ${
        hasNewActivity ? "border-l-4 border-l-blue-500" : "border-border"
      }`}
      data-no-drag="true"
      data-first-post={index === 0 ? "true" : undefined}
      onClick={() => onPostClick(post)}
    >
      {/* Admin buttons positioned at top-right */}
      {isAdmin && (
        <div className="absolute top-2 right-2 flex gap-1 z-10">
          <Button
            size="sm"
            variant="ghost"
            onClick={(event) => onTogglePublish(post, event)}
            className="text-xs px-2 h-7 bg-background/80 backdrop-blur-sm"
          >
            {post.published ? "Unpublish" : "Publish"}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={(event) => {
              event.stopPropagation();
              onEditPost(post);
            }}
            className="h-7 w-7 p-0 bg-background/80 backdrop-blur-sm"
          >
            <Edit className="w-3 h-3" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={(event) => onDeletePost(post, event)}
            className="h-7 w-7 p-0 bg-background/80 backdrop-blur-sm"
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      )}

      <div className={`flex flex-col sm:flex-row gap-4 ${index % 2 === 1 ? "sm:flex-row-reverse" : ""}`}>
        <div className="flex flex-col items-center sm:items-start flex-shrink-0">
          <div className="w-36 h-36 overflow-hidden rounded bg-muted/30">
            {post.image_url ? (
              <img
                src={post.image_url.replace(/\.(\w+)$/, "-small.png")}
                alt={post.title}
                className="w-full h-full object-cover"
                loading="lazy"
                width={144}
                height={144}
                onError={(e) => {
                  e.currentTarget.src = post.image_url!;
                }}
              />
            ) : (
              <div className="w-full h-full bg-muted/50 flex items-center justify-center opacity-30">
                <Logo size={32} className="grayscale" />
              </div>
            )}
          </div>
        </div>

        <div className="flex-1 min-w-0">
          {/* Title on its own line */}
          <h3 className="font-semibold text-sm mb-2">{post.title}</h3>
          
          {/* Counts and social links */}
          <PostLikes
            postId={post.id}
            content={post.content}
            post={post}
            isDetailView={false}
            metrics={getMetrics(post.id)}
            onPostClick={onPostClick}
            onHasNewActivity={setHasNewActivity}
            getSocialIcon={getSocialIcon}
            compactMode={true}
          />
          
          <p className="text-xs text-muted-foreground mt-2 mb-2">{formatDate(post.published_at || post.created_at)}</p>
          <p className="text-xs text-muted-foreground line-clamp-5 md:line-clamp-3">{post.preview}</p>
          {post.post_tags && post.post_tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {post.post_tags.slice(0, 5).map((pt) => (
                <button
                  key={pt.tag_id}
                  onClick={(event) => {
                    event.stopPropagation();
                    onTagClick(pt.tags.slug);
                  }}
                  className="inline-flex items-center gap-1 bg-primary/10 text-primary border border-primary/20 rounded-full px-2 py-0.5 text-xs hover:bg-primary/20 transition-colors"
                >
                  <TagIcon className="w-2.5 h-2.5" />
                  {pt.tags.name}
                </button>
              ))}
              {post.post_tags.length > 5 && (
                <span className="text-xs text-muted-foreground">+{post.post_tags.length - 5} more</span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default memo(BlogPostListItem);
